import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';
import Dot from './Dot/Dot';
import Tick from './Tick/Tick';

interface IData {
  kpi: number;
  endDate: string | Date;
  type: 'ADAPT' | 'PULSE';
}

interface IKPIHistoryChart {
  data: IData[];
  language: string;
  target?: number;
}

const KPIHistoryChart: FC<IKPIHistoryChart> = ({ data, language, target }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const max = Math.max(...data.map(({ kpi }) => kpi));
  const min = Math.min(...data.map(({ kpi }) => kpi));
  const domain = target
    ? [Math.min(min - 2, target - 2), Math.max(max + 2, target + 2)]
    : [min - 2, max + 2];

  const padding =
    data.length === 2 || !target ? { left: 80, right: 80 } : { left: 46, right: 32.5 };

  return (
    <ResponsiveContainer width="100%" height={244}>
      <LineChart margin={{ top: 12, right: 5, bottom: -16, left: 5 }} {...{ data }}>
        <CartesianGrid
          horizontal={false}
          stroke={theme.colors.neutral.primary[20]}
          strokeDasharray="10"
          syncWithTicks
        />
        <XAxis
          axisLine={false}
          dataKey="endDate"
          padding={padding}
          tick={props => <Tick {...{ ...props, language }} />}
          tickLine={false}
          tickSize={12}
          xAxisId="0"
        />
        <XAxis
          axisLine={false}
          dataKey="type"
          padding={padding}
          tick={props => <Tick {...{ ...props, language }} />}
          tickLine={false}
          tickSize={8}
          xAxisId="1"
        />
        <YAxis domain={domain} padding={{ top: 12, bottom: 12 }} hide />
        {target && (
          <ReferenceLine
            y={60}
            label={{
              position: 'insideBottomLeft',
              dy: -12,
              cx: 100,
              value: t('report.kpi.target', { target }),
              fontSize: 12,
              fontWeight: 500,
              fill: theme.colors.neutral.primary[80],
            }}
            stroke={'#BFBFBF'}
            strokeDasharray="10"
            strokeWidth={2}
          />
        )}

        <Line
          dataKey="kpi"
          type="bump"
          dot={props => <Dot {...{ ...props }} />}
          animationDuration={250}
          stroke={theme.colors.neutral.primary[50]}
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default KPIHistoryChart;
